import { useMemo, useState } from "react";

/**
 * Interaktiv månadskostnadskalkyl för husbil.
 * Rendas inline i blogginlägg via markörer i markdown.
 */
export function BlogTcoCalculator() {
  const [price, setPrice] = useState(650_000);
  const [downPct, setDownPct] = useState(20);
  const [years, setYears] = useState(7);
  const [rate, setRate] = useState(6.9);
  const [km, setKm] = useState(5_000);
  const [storage, setStorage] = useState<"ute" | "tak" | "inne">("tak");

  const calc = useMemo(() => {
    const down = Math.round((price * downPct) / 100);
    const loan = price - down;
    const months = years * 12;
    const r = rate / 100 / 12;
    const monthlyLoan =
      r === 0 ? loan / months : (loan * r) / (1 - Math.pow(1 + r, -months));
    const skatt = price > 800_000 ? 5800 : price > 500_000 ? 4200 : 2400;
    const forsakring = price > 800_000 ? 12000 : price > 500_000 ? 8500 : 6500;
    const service = 4800;
    const fukt = 1495;
    const forvaring = storage === "inne" ? 8000 : storage === "tak" ? 4000 : 0;
    const bransle = Math.round((km / 100) * 1.25 * 17.5); // ~1,25 l/mil * 17,5 kr/l
    const daryear = skatt + forsakring + service + fukt + forvaring + bransle;
    const monthlyFixed = daryear / 12;
    const total = monthlyLoan + monthlyFixed;
    return {
      down,
      loan,
      monthlyLoan,
      skatt,
      forsakring,
      service,
      fukt,
      forvaring,
      bransle,
      daryear,
      monthlyFixed,
      total,
    };
  }, [price, downPct, years, rate, km, storage]);

  const fmt = (n: number) =>
    new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(
      Math.round(n),
    );

  return (
    <section
      aria-label="Månadskostnadskalkyl husbil"
      className="not-prose my-12 rounded-3xl border border-accent/30 bg-gradient-to-br from-accent/5 to-transparent p-6 md:p-8"
    >
      <p className="eyebrow text-accent">Interaktiv kalkyl</p>
      <h3 className="mt-3 font-display text-3xl leading-tight md:text-4xl">
        Vad kostar din husbil <span className="font-italic-display text-accent">per månad</span>?
      </h3>
      <p className="mt-2 text-sm text-muted-foreground">
        Skjut på reglagen. Kalkylen räknar lån, drift och förvaring i realtid — utan att spara något.
      </p>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <div className="space-y-5">
          <Field label="Inköpspris" value={`${fmt(price)} kr`}>
            <input type="range" min={150_000} max={2_000_000} step={10_000}
              value={price} onChange={(e) => setPrice(+e.target.value)} className="w-full accent-accent" />
          </Field>
          <Field label="Kontantinsats" value={`${downPct}% (${fmt(calc.down)} kr)`}>
            <input type="range" min={0} max={80} step={5}
              value={downPct} onChange={(e) => setDownPct(+e.target.value)} className="w-full accent-accent" />
          </Field>
          <Field label="Löptid" value={`${years} år`}>
            <input type="range" min={3} max={12} step={1}
              value={years} onChange={(e) => setYears(+e.target.value)} className="w-full accent-accent" />
          </Field>
          <Field label="Ränta" value={`${rate.toFixed(1)}%`}>
            <input type="range" min={3} max={12} step={0.1}
              value={rate} onChange={(e) => setRate(+e.target.value)} className="w-full accent-accent" />
          </Field>
          <Field label="Körsträcka/år" value={`${fmt(km)} km`}>
            <input type="range" min={1_000} max={20_000} step={500}
              value={km} onChange={(e) => setKm(+e.target.value)} className="w-full accent-accent" />
          </Field>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Förvaring vinter</p>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {(["ute", "tak", "inne"] as const).map((s) => (
                <button key={s} type="button" onClick={() => setStorage(s)}
                  className={`rounded-full border px-3 py-2 text-xs font-medium transition ${
                    storage === s
                      ? "border-accent bg-accent text-accent-foreground"
                      : "border-border bg-card hover:border-accent/50"
                  }`}>
                  {s === "ute" ? "Ute" : s === "tak" ? "Tak" : "Inne"}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 md:p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-accent">Total månadskostnad</p>
          <p className="mt-2 font-display text-5xl leading-none tracking-tight md:text-6xl">
            {fmt(calc.total)} <span className="text-2xl text-muted-foreground">kr/mån</span>
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            varav lån {fmt(calc.monthlyLoan)} kr · drift {fmt(calc.monthlyFixed)} kr
          </p>

          <dl className="mt-5 space-y-2 text-sm">
            <Row k="Lånebelopp" v={`${fmt(calc.loan)} kr`} />
            <Row k="Fordonsskatt/år" v={`${fmt(calc.skatt)} kr`} />
            <Row k="Försäkring/år" v={`${fmt(calc.forsakring)} kr`} />
            <Row k="Service + kontroll/år" v={`${fmt(calc.service + calc.fukt)} kr`} />
            <Row k="Förvaring/år" v={`${fmt(calc.forvaring)} kr`} />
            <Row k="Bränsle/år" v={`${fmt(calc.bransle)} kr`} />
            <Row k="Driftkostnad totalt/år" v={`${fmt(calc.daryear)} kr`} strong />
          </dl>

          <p className="mt-4 text-[11px] leading-relaxed text-muted-foreground">
            Riktvärden baserade på svenska handlarpriser 2026. Räkna med ±15 % beroende på modell,
            försäkringsbolag och boendekommun. Ingen personlig kreditbedömning.
          </p>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, children }: { label: string; value: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className="font-display text-sm text-foreground">{value}</span>
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Row({ k, v, strong }: { k: string; v: string; strong?: boolean }) {
  return (
    <div className={`flex justify-between border-b border-border/50 pb-1.5 ${strong ? "font-semibold text-foreground" : "text-foreground/80"}`}>
      <dt>{k}</dt>
      <dd className="tabular-nums">{v}</dd>
    </div>
  );
}
